import { useEffect } from 'react';
import { Card, Stack, Switch } from '@mantine/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ConfigurationStepsField } from '@components/ui/Form/ConfigurationStep';
import { useAuthUser } from '@store/useAuthStore';

import { canUserConfigureReturnToWaitingRoom } from '../../constants';
import { useSessionCreationContext } from '../../SessionCreationContext';
import { SessionConfig } from '../../utils/experienceForm/transform';

export const EMPTY_SESSION_CONFIG: SessionConfig = {
  id: '',
  name: '',
  description: '',
  returnToWaitingRoom: false,
  steps: [],
};

type SessionConfigFormProps = {
  onSubmit: (values: SessionConfig) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

export function SessionConfigForm({
  onSubmit,
  onDirtyChange,
}: SessionConfigFormProps) {
  const form = useFormContext<SessionConfig>();

  if (!form) {
    throw new Error('SessionConfigForm must be used within a FormProvider');
  }

  const { control, handleSubmit, trigger, getValues, formState, setValue } =
    form;
  const { isDirty, isValid } = formState;
  const values = useWatch({ control });
  const authUser = useAuthUser();
  const showReturnToWaitingRoom = canUserConfigureReturnToWaitingRoom(
    authUser?.id
  );

  useEffect(() => {
    if (!showReturnToWaitingRoom) {
      setValue('returnToWaitingRoom', false, { shouldDirty: false });
    }
  }, [showReturnToWaitingRoom, setValue]);

  const {
    setStepValue,
    setStepValidity,
    registerStepValidator,
    unregisterStepValidator,
  } = useSessionCreationContext();

  // 🔄 Mantieni il contesto sincronizzato con i valori del form
  useEffect(() => {
    setStepValue('configuration', values);
    setStepValidity('configuration', isValid);
    onDirtyChange?.(isDirty);
  }, [values, isDirty, isValid, setStepValue, setStepValidity, onDirtyChange]);

  // ✅ Registrazione del validatore nel contesto
  useEffect(() => {
    const validator = async () => {
      const valid = await trigger();
      setStepValidity('configuration', valid);
      if (valid) setStepValue('configuration', getValues());
      return valid;
    };

    registerStepValidator('configuration', validator);

    return () => {
      unregisterStepValidator('configuration');
      setStepValidity('configuration', false);
      setStepValue('configuration', null);
    };
  }, [
    registerStepValidator,
    unregisterStepValidator,
    trigger,
    getValues,
    setStepValidity,
    setStepValue,
  ]);

  // const handleReset = () => {
  //   reset({ id: '', name: '', description: '', steps: [] });
  //   setStepValue('configuration', null);
  //   setStepValidity('configuration', false);
  // };

  return (
    <form
      onSubmit={handleSubmit(data => {
        onSubmit(data);
      })}
      className='flex flex-col gap-4'
    >
      <Stack className='space-y-1'>
        {showReturnToWaitingRoom ? (
          <Card withBorder mt='md' p='sm' bg='gray.0'>
            <Stack gap='sm'>
              <Controller
                control={control}
                name='returnToWaitingRoom'
                render={({ field }) => (
                  <Switch
                    label='Ritorna alla waiting room al termine della sessione'
                    checked={Boolean(field.value)}
                    onChange={event =>
                      field.onChange(event.currentTarget.checked)
                    }
                  />
                )}
              />
            </Stack>
          </Card>
        ) : null}
        <Card withBorder mt='md' p='sm' bg='gray.0'>
          <ConfigurationStepsField name='steps' />
        </Card>
      </Stack>
    </form>
  );
}
